import { type FC, Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { CompanyUserRole } from "shared";
import { useCompany } from "../company/useCompany";
import { Card } from "../components/Card";
import { useRegisterMetaAdsUnsavedWork } from "./metaAdsUnsavedWork";
import { useApprovalActions, useApprovalSettings } from "./useApprovals";
import { useCanManageMetaAds } from "./useMyMetaAdsRole";

const ApprovalSettingsInner: FC = () => {
	const company = useCompany();
	const { data: settings } = useApprovalSettings();
	const { updateApprovalEnabled, setApprovers } = useApprovalActions();

	// Get eligible approvers: CLIENT_ACCOUNT or MEMBER users who also have Manager Meta Ads role
	// For simplicity, we show all company users here and let the API enforce
	const eligibleUsers = company.users.filter(
		(u) =>
			u.role === CompanyUserRole.OWNER ||
			u.role === CompanyUserRole.MEMBER ||
			u.role === CompanyUserRole.CLIENT_ACCOUNT,
	);

	const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(
		() => new Set(settings.approvers.map((a) => a.userId)),
	);

	const handleToggle = async () => {
		if (settings.enabled) {
			updateApprovalEnabled.mutate(false);
		} else {
			if (selectedUserIds.size === 0) {
				return;
			}
			updateApprovalEnabled.mutate(true);
		}
	};

	const handleUserToggle = (userId: number) => {
		setSelectedUserIds((prev) => {
			const next = new Set(prev);
			if (next.has(userId)) {
				next.delete(userId);
			} else {
				next.add(userId);
			}
			return next;
		});
	};

	const handleSaveApprovers = () => {
		setApprovers.mutate({ userIds: Array.from(selectedUserIds) });
	};

	const hasChanges = (() => {
		const currentIds = new Set(settings.approvers.map((a) => a.userId));
		if (currentIds.size !== selectedUserIds.size) return true;
		const selectedArr = Array.from(selectedUserIds);
		for (const id of selectedArr) {
			if (!currentIds.has(id)) return true;
		}
		return false;
	})();

	useRegisterMetaAdsUnsavedWork(
		hasChanges,
		"Unsaved Meta Ads approval settings",
	);

	return (
		<Card>
			<Card.Header>Approval Workflow</Card.Header>
			<Card.Body>
				<div className="form-check form-switch mb-3">
					<input
						className="form-check-input"
						type="checkbox"
						id="approval-toggle"
						checked={settings.enabled}
						onChange={handleToggle}
						disabled={
							updateApprovalEnabled.isPending ||
							(!settings.enabled && selectedUserIds.size === 0)
						}
					/>
					<label className="form-check-label" htmlFor="approval-toggle">
						Require campaign approvals before publishing
					</label>
				</div>

				<h6>Designated Approvers</h6>
				{eligibleUsers.length === 0 ? (
					<p className="text-muted">No eligible users found.</p>
				) : (
					<div style={{ maxHeight: 200, overflow: "auto" }}>
						{eligibleUsers.map((user) => (
							<div key={user.id} className="form-check">
								<input
									className="form-check-input"
									type="checkbox"
									id={`approver-${user.id}`}
									checked={selectedUserIds.has(user.id)}
									onChange={() => handleUserToggle(user.id)}
								/>
								<label
									className="form-check-label"
									htmlFor={`approver-${user.id}`}
								>
									{user.firstName} {user.lastName} ({user.email})
								</label>
							</div>
						))}
					</div>
				)}
				{hasChanges && (
					<button
						type="button"
						className="btn btn-sm btn-primary mt-2"
						onClick={handleSaveApprovers}
						disabled={setApprovers.isPending}
					>
						{setApprovers.isPending ? "Saving..." : "Save Approvers"}
					</button>
				)}
				{!settings.enabled && selectedUserIds.size === 0 && (
					<p className="text-muted mt-2" style={{ fontSize: 13 }}>
						Add at least one approver before enabling the workflow.
					</p>
				)}
			</Card.Body>
		</Card>
	);
};

export const MetaAdsApprovalSettings: FC = () => {
	const canManage = useCanManageMetaAds();

	if (!canManage) return null;

	return (
		<ErrorBoundary fallback={null}>
			<Suspense fallback={<p>Loading approval settings...</p>}>
				<ApprovalSettingsInner />
			</Suspense>
		</ErrorBoundary>
	);
};
